import $ from 'jquery';
import { Controller } from 'stimulus';
import { FetchRequest } from '@rails/request.js';
import { Meta, templateContent, useEventListener } from '@/js/util';
import { beforeStreamRender } from '@/js/util/turbo';
import Util from '@/js/editor/util';
import confirmDialog from '@/js/modules/confirm_dialog';

const arrayWrap = (object) => (Array.isArray(object) ? object : new Array(object));

const DESIGN_ELEMENT_TYPES = { board: 'Board', fpview: 'FpView', folder: 'Folder' };

const REORDER_ITEMS = {
  combined_ids: ({ boardType, boardId }) => `${boardType}-${boardId}`,
  design_elements: ({ boardType, boardId }) => ({
    design_element_type: DESIGN_ELEMENT_TYPES[boardType],
    design_element_id: boardId,
  }),
};

class BoardLoading {
  static get element() {
    return document.querySelector('.board-loading');
  }

  static show() {
    this.element.style.display = 'flex';
  }

  static hide() {
    this.element.style.display = 'none';
  }
}

class EditorElements {
  static get snapshotsFrame() {
    return document.querySelector('turbo-frame#editor_snapshots_dropdown');
  }

  // Template edit mode has no snapshots dropdown
  static set snapshotsFrameSrc(value) {
    this.snapshotsFrame?.setAttribute('src', value);
  }

  static get boardNameContainer() {
    return document.querySelector('[data-editor-content="board-name"]');
  }

  static set boardName(value) {
    this.boardNameContainer.textContent = value;
  }

  static get backButton() {
    return document.querySelector('[data-editor-button="save-and-exit"]');
  }

  static set backButtonURL(value) {
    this.backButton?.setAttribute('href', value);
  }

  static get templateBackButton() {
    return document.querySelector('[data-editor-button="back-to-app"]');
  }

  static set templateBackButtonURL(value) {
    this.templateBackButton?.setAttribute('href', value);
  }
}

export default class extends Controller {
  static targets = ['board', 'boards', 'chevron', 'loadingBoardTemplate'];
  static outlets = ['moodboard--drawer--learn-button'];
  static classes = ['currentBoard', 'removing'];
  static values = {
    isPanelOpened: Boolean,
    currentBoardId: Number,
    // Omit to disable reordering.
    reorderPath: String,
    reorderMethod: { type: String, default: 'POST' },
    reorderParam: { type: String, default: 'combined_ids' },
    boardType: { type: String, default: 'board' },
    backUrl: String,
    deleteTitle: String,
    deleteMessage: String,
    deleteButton: String,
  };

  connect() {
    this.connected = true;
    this.dragging = false;
    this.animateRemoval = this.animateRemoval.bind(this);

    useEventListener(this);
    this.addEventListener(
      document,
      'turbo:before-stream-render',
      beforeStreamRender(this.animateRemoval)
    );

    this.#showDropdownTriggers(this.boardTargets);
    this.dispatchBoardChangedEvent();
  }

  async animateRemoval(stream) {
    const board = this.boardTargets.find((element) => element.id === stream.target);
    if (!this.hasRemovingClass || stream.action !== 'remove' || !board) {
      return;
    }

    board.classList.add(...this.removingClasses);
    await Promise.all(board.getAnimations().map((animation) => animation.finished));
  }

  trackSwitch() {
    if (this.boardTypeValue !== 'board') {
      return;
    }

    if (this.currentBoardIdValue !== parseInt(window.ed.boardId)) {
      window.ed.featureActionsService.add('boards_panel_design_switched');
    }
  }

  boardsTargetConnected(element) {
    this.#setupReordering(element);
  }

  async boardTargetConnected(element) {
    this.#showDropdownTriggers(element);

    if (!this.connected) {
      return;
    }

    this.#highlight(element);

    if (element.getAttribute('data-switch-to') !== 'true') {
      return;
    }

    element.setAttribute('data-switch-to', 'false');

    if (!(await this.#allowedToSwitch())) {
      return;
    }

    this.currentBoardIdValue = element.getAttribute('data-board-id');
  }

  dispatchBoardChangedEvent() {
    this.dispatch('board-changed', { detail: { boardId: this.currentBoardIdValue } });
  }

  isPanelOpenedValueChanged(value) {
    this.element.classList.toggle('translate-y-[calc(100%-18px)]', !value);
    this.chevronTarget.classList.toggle('rotate-180', value);

    if (this.hasMoodboardDrawerLearnButtonOutlet) {
      this.moodboardDrawerLearnButtonOutlet.adjustForBoardsPane(value);
    }

    this.dispatch('toggled', { detail: { open: value }, target: document });
  }

  currentBoardIdValueChanged(boardId) {
    if (!this.connected) {
      return;
    }

    BoardLoading.show();

    let currentBoardEl;
    this.boardTargets.forEach((element) => {
      if (this.#highlight(element)) {
        currentBoardEl = element;
      }
    });

    window.history.pushState({}, '', currentBoardEl.getAttribute('action'));

    window.ed.boardId = boardId;
    window.ed._io.clearBoard();
    window.ed._guiZoom.fitToScreen();
    window.ed._io.loadBoard();

    this.#updateUI(currentBoardEl);
    this.dispatchBoardChangedEvent();
  }

  /**
   * @param {CustomEvent} e
   */
  addLoadingTemplate({ detail }) {
    this.isPanelOpenedValue = true;
    const { domIds } = detail;
    const lastBoard = domIds.reduce((_, domId) => {
      const board = templateContent(this.loadingBoardTemplateTarget);
      board.id = domId;
      this.boardsTarget.appendChild(board);
      return board;
    }, null);
    lastBoard.scrollIntoView({ behavior: 'smooth', inline: 'center' });
  }

  /**
   * @param {CustomEvent} e
   */
  async deleteConfirmation(e) {
    const { target } = e;
    const boardId = target.getAttribute('data-board-id');
    const boardType = target.getAttribute('data-board-type').replace('_', '');
    const findBoard = () =>
      this.boardTargets.find(
        (el) =>
          el.getAttribute('data-board-type') === boardType &&
          el.getAttribute('data-board-id') === boardId
      );

    if (!findBoard()) {
      return;
    }

    await confirmDialog(this.deleteMessageValue, {
      title: this.deleteTitleValue,
      button: this.deleteButtonValue,
      icon: 'trash',
      onConfirm: () => {
        const board = findBoard();
        if (board && window.ed) {
          return this.#delete(board);
        }
      },
    });
  }

  open() {
    this.isPanelOpenedValue = true;
  }

  toggleListPanel() {
    if (!this.isPanelOpenedValue) {
      window.ed?.featureActionsService.add('boards_panel_opened');
    }

    this.isPanelOpenedValue = !this.isPanelOpenedValue;
  }

  /**
   * @param {MouseEvent|TouchEvent} e
   */
  async switchBoard(e) {
    // Lookbook has no editor.
    if (!window.ed) {
      e.preventDefault();
      return;
    }

    if (this.dragging) {
      e.preventDefault();
      return;
    }

    const { currentTarget } = e;

    if (currentTarget.parentElement.getAttribute('data-board-type') === 'fpview') {
      return;
    } else {
      e.preventDefault();
    }

    if (!(await this.#allowedToSwitch())) {
      return;
    }

    this.currentBoardIdValue = currentTarget.parentElement.getAttribute('data-board-id');
  }

  #delete(selectedBoard) {
    const isCurrent =
      selectedBoard.getAttribute('data-board-type') === this.boardTypeValue &&
      this.currentBoardIdValue === parseInt(selectedBoard.getAttribute('data-board-id'));

    if (isCurrent) {
      BoardLoading.show();
    }

    const moodboardIds = this.#allMoodboardIds;
    const url = selectedBoard.getAttribute('data-destroy-board-path');
    const responseKind = 'turbo-stream';
    const request = new FetchRequest('DELETE', url, { responseKind });

    return request
      .perform()
      .then(() => {
        if (!isCurrent) {
          return;
        }

        if (moodboardIds.length === 1) {
          return this.hasBackUrlValue
            ? window.location.assign(this.backUrlValue)
            : this.#createBlankBoard();
        }

        const currentBoardIndex = moodboardIds.indexOf(selectedBoard.getAttribute('data-board-id'));
        const newBoardIndex = currentBoardIndex === 0 ? 1 : currentBoardIndex - 1;
        this.currentBoardIdValue = moodboardIds[newBoardIndex];
      })
      .catch((error) => {
        isCurrent && BoardLoading.hide();
        console.error(error);
      });
  }

  #highlight(element) {
    if (element.getAttribute('data-board-type') !== this.boardTypeValue) {
      return false;
    }

    const isCurrent = parseInt(element.getAttribute('data-board-id')) === this.currentBoardIdValue;
    const triggerElement = element.querySelector('button[type="submit"]');
    this.currentBoardClasses.forEach((currentClass) => {
      triggerElement.classList.toggle(currentClass, isCurrent);
    });
    return isCurrent;
  }

  #showDropdownTriggers(elements) {
    if (!Util.isTouchScreen()) {
      return;
    }

    for (const element of arrayWrap(elements)) {
      element
        .querySelector('button[data-controller="moodboard--dropdown-trigger"]')
        ?.classList.remove('hidden');
    }
  }

  #setupReordering(boards) {
    if (!this.reorderPathValue) {
      return;
    }

    const start = (_event, _ui) => {
      this.dragging = true;
    };

    const stop = (_event, _ui) => {
      setTimeout(() => {
        if (this.boardTypeValue === 'board') {
          window.ed.featureActionsService.add('boards_panel_design_reordered');
        }
        this.dragging = false;
      }, 100);

      const toItem = REORDER_ITEMS[this.reorderParamValue];
      const items = this.boardTargets.map((element) => toItem(element.dataset));

      const options = { body: { [this.reorderParamValue]: items } };
      const request = new FetchRequest(this.reorderMethodValue, this.reorderPathValue, options);
      request.perform().catch(console.error);
    };

    $(boards).sortable({
      start,
      stop,
      delay: 150,
      cancel: 'input,button[type="button"]',
    });
  }

  #updateUI(currentBoardEl) {
    Meta.set('board-id', this.currentBoardIdValue);
    EditorElements.boardName = currentBoardEl.getAttribute('title');

    // Boards go back to their show page; templates to the slide now open.
    const snapshotsPath = currentBoardEl.getAttribute('data-snapshots-frame-path');
    const showBoardPath = currentBoardEl.getAttribute('data-show-board-path');
    const templateBackPath = currentBoardEl.getAttribute('data-back-path');
    if (snapshotsPath) {
      EditorElements.snapshotsFrameSrc = snapshotsPath;
    }
    if (showBoardPath) {
      EditorElements.backButtonURL = showBoardPath;
    }
    if (templateBackPath) {
      EditorElements.templateBackButtonURL = templateBackPath;
    }
  }

  get #allMoodboardIds() {
    return this.boardTargets.reduce((acc, element) => {
      if (element.getAttribute('data-board-type') !== this.boardTypeValue) {
        return acc;
      }
      acc.push(element.getAttribute('data-board-id'));
      return acc;
    }, []);
  }

  #allowedToSwitch() {
    // beforeunload does not work with history API, so we kinda simulating it
    return (
      !window.onbeforeunload ||
      confirmDialog('Changes that you made may not be saved.', {
        title: 'Unsaved Changes',
        button: 'Switch Anyway',
      })
    );
  }

  #createBlankBoard() {
    document.querySelector('.board-types__type--2d').click();
  }
}
